import { priceRangeCode } from '../../../config/productFilters';
import type { RangeFilterValue } from '../state/range';
import { makeContinuousRangeValue, RangeFilterStateMap } from '../state/range';
import { extractAttributeCode, matchAttributePattern, prefixAttribute } from './attributeCode';
import { BigDecimal, HashMap, Option, Schema, SchemaGetter } from 'effect';

const rangeFilterStateMapToApiRecord = (
  filterStateMap: RangeFilterStateMap
): Record<string, string> =>
  HashMap.reduce(filterStateMap, {} as Record<string, string>, (acc, rangeValue, attributeCode) => {
    if (attributeCode === priceRangeCode) {
      // Price uses minor units (cents) and no attribute_ prefix
      // Convert BigDecimal to cents for API using BigDecimal multiplication
      const hundred = BigDecimal.fromNumberUnsafe(100);
      const minCents = BigDecimal.multiply(rangeValue.min, hundred);
      const maxCents = BigDecimal.multiply(rangeValue.max, hundred);

      // Floor min to be inclusive of lower prices, ceil max to be inclusive of higher prices
      // This ensures products with prices like 125.99 are included when filtering up to 126.00
      const minCentsFloored = BigDecimal.floor(minCents);
      const maxCentsCeiled = BigDecimal.ceil(maxCents);

      acc['price[gte]'] = String(BigDecimal.toNumberUnsafe(minCentsFloored));
      acc['price[lte]'] = String(BigDecimal.toNumberUnsafe(maxCentsCeiled));
    } else {
      // Other attributes use attribute_ prefix
      // Floor min, ceil max for inclusive range (same strategy as price)
      const attributeName = prefixAttribute(attributeCode);

      const minFloored = BigDecimal.floor(rangeValue.min);
      const maxCeiled = BigDecimal.ceil(rangeValue.max);

      acc[`${attributeName}[gte]`] = String(BigDecimal.toNumberUnsafe(minFloored));
      acc[`${attributeName}[lte]`] = String(BigDecimal.toNumberUnsafe(maxCeiled));
    }
    return acc;
  });

const apiRecordToRangeFilterStateMap = (apiRecord: Record<string, string>): RangeFilterStateMap => {
  let result = HashMap.empty<string, RangeFilterValue>();
  const rangeMap: Record<string, { min?: number; max?: number }> = {};

  // Parse [gte]/[lte] parameters and group by attribute
  for (const [key, value] of Object.entries(apiRecord)) {
    const gteMatch = matchAttributePattern(/^(.+)\[gte\]$/)(key);
    const lteMatch = matchAttributePattern(/^(.+)\[lte\]$/)(key);

    if (Option.isSome(gteMatch) && gteMatch.value[1]) {
      const attributeName = gteMatch.value[1];

      if (attributeName === 'price') {
        // Price comes from API in minor units (cents), convert to major units using BigDecimal division
        rangeMap[priceRangeCode] ??= {};
        const valueBD = BigDecimal.fromNumberUnsafe(Number(value));
        const hundred = BigDecimal.fromNumberUnsafe(100);
        const divideResult = BigDecimal.divide(valueBD, hundred);
        // Division by 100 should always succeed, fallback to 0 if not
        rangeMap[priceRangeCode].min = Option.isSome(divideResult)
          ? BigDecimal.toNumberUnsafe(divideResult.value)
          : 0;
      } else {
        const code = extractAttributeCode(attributeName);
        rangeMap[code] ??= {};
        rangeMap[code].min = Number(value);
      }
    } else if (Option.isSome(lteMatch) && lteMatch.value[1]) {
      const attributeName = lteMatch.value[1];

      if (attributeName === 'price') {
        // Price comes from API in minor units (cents), convert to major units using BigDecimal division
        rangeMap[priceRangeCode] ??= {};
        const valueBD = BigDecimal.fromNumberUnsafe(Number(value));
        const hundred = BigDecimal.fromNumberUnsafe(100);
        const divideResult = BigDecimal.divide(valueBD, hundred);
        // Division by 100 should always succeed, fallback to 0 if not
        rangeMap[priceRangeCode].max = Option.isSome(divideResult)
          ? BigDecimal.toNumberUnsafe(divideResult.value)
          : 0;
      } else {
        const code = extractAttributeCode(attributeName);
        rangeMap[code] ??= {};
        rangeMap[code].max = Number(value);
      }
    }
  }

  // Create RangeFilterState for complete ranges
  // TODO: we should support even if only one of min/max is set
  for (const [code, range] of Object.entries(rangeMap)) {
    if (range.min !== undefined && range.max !== undefined) {
      result = HashMap.set(
        result,
        code,
        makeContinuousRangeValue({
          min: BigDecimal.fromNumberUnsafe(range.min),
          max: BigDecimal.fromNumberUnsafe(range.max),
        })
      );
    }
  }

  return result;
};

export const RangeApiQuery = Schema.Record(Schema.String, Schema.String);

export const RangeToApi = Schema.toType(RangeFilterStateMap).pipe(
  Schema.decodeTo(RangeApiQuery, {
    decode: SchemaGetter.transform(rangeFilterStateMapToApiRecord),
    encode: SchemaGetter.transform(apiRecordToRangeFilterStateMap),
  })
);
